import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Stack } from '@mui/material';
import PageHeader from 'components/Header/Page';
import { useLocation, useNavigate } from 'react-router-dom';
import moment from 'moment';
import { CustomizedDataGrid } from 'components/customized_data_grid';
import LoaderComponent from 'components/Loader';
import PageWrapper from 'components/Common/PageWraper';
import FiltersBar from './FilterBar';
import QueryBuilder from './QueryBuilder';
import { QueryRule } from 'utils/adb-common-util';
import { fetchClusterNameBasedOnWorkspace, fetchSectorList, getWorkspaceOptions, workspaceGetEnv } from 'service/service';

const defaultColumnNames = [
    'Workspace ID',
    'Workspace Name',
    'Created Date',
    'Compute Type',
    'Cluster Name',
    'Cluster ID'
];

const computeTypeOptions = [
    { label: 'All', value: 'All' },
    { label: 'INTERACTIVE', value: 'INTERACTIVE' },
    { label: 'JOB', value: 'JOB' },
    { label: 'SQL', value: 'SQL' }
];

function getColumns() {
    return [
        {
            key: 'workspaceId',
            name: 'Workspace ID',
            formatter({ row }: any) {
                return <div title={row?.workspaceId}>{row?.workspaceId || '--'}</div>;
            },
            minWidth: 160
        },
        {
            key: 'workspaceName',
            name: 'Workspace Name',
            formatter({ row }: any) {
                return <div title={row?.workspaceName}>{row?.workspaceName || '--'}</div>;
            },
            minWidth: 160
        },
        {
            key: 'date',
            name: 'Created Date',
            formatter({ row }: any) {
                const d = row?.date;
                return <div>{d ? moment.utc(d).format('YYYY-MM-DD') : '--'}</div>;
            },
            minWidth: 140
        },
        {
            key: 'clusterSource',
            name: 'Compute Type',
            formatter({ row }: any) {
                return <div>{(row?.computeType || '--').toString().toUpperCase()}</div>;
            },
            minWidth: 120
        },
        {
            key: 'clusterName',
            name: 'Cluster Name',
            formatter({ row }: any) {
                return <div title={row?.clusterName}>{row?.clusterName || '--'}</div>;
            },
            minWidth: 320
        },
        {
            key: 'clusterId',
            name: 'Cluster ID',
            formatter({ row }: any) {
                return <div title={row?.clusterId}>{row?.clusterId || '--'}</div>;
            },
            minWidth: 180
        }
    ];
}

interface ClusterDetailsTableBaseProps {
    serviceFunc: any;
    pageSubHeaderDefault: string;
    preserveFilterKey: string;
    tableType: string;
    backPath: string;
    backBreadcrumbName: string;
}

const ClusterDetailsTableBase: React.FC<ClusterDetailsTableBaseProps> = ({
    serviceFunc,
    pageSubHeaderDefault,
    preserveFilterKey,
    tableType,
    backPath,
    backBreadcrumbName
}) => {
    const normalizeComputeType = (v?: any) => {
        if (v === undefined || v === null) return undefined;
        const s = String(v).trim();
        return s ? s.toUpperCase() : undefined;
    };
    const routerLocation = useLocation();
    const navigate = useNavigate();
    const navState = routerLocation.state;

    const yesterday = useMemo(() => moment().tz('Asia/Kolkata').subtract(1, 'day'), []);
    const defStartDate = useMemo(() => yesterday.clone().subtract(3, 'months').startOf('day'), [yesterday]);
    const DEFAULT_FILTERS = useMemo(() => ({
        startDate: navState?.startDate || defStartDate.format('YYYY-MM-DD'),
        endDate: navState?.endDate || yesterday.endOf('day').format('YYYY-MM-DD'),
        env: navState?.env || 'both',
        sector: navState?.sector || 'all',
        location: navState?.location || 'South Central US',
        selectedDateInString: navState?.selectedDateInString || '3M'
    }), [navState, defStartDate, yesterday]);

    const restoredRef = useRef(false);
    const initialEnvAppliedRef = useRef(false);

    const [startDate, setStartDate] = useState<string>(DEFAULT_FILTERS.startDate);
    const [endDate, setEndDate] = useState<string>(DEFAULT_FILTERS.endDate);
    const [selectedDateInString, setSelectedDateInString] = useState<string>(DEFAULT_FILTERS.selectedDateInString);
    const [sector, setSector] = useState<string>(DEFAULT_FILTERS.sector);
    const [locationFilter, setLocationFilter] = useState<string>(DEFAULT_FILTERS.location);
    const [env, setEnv] = useState<string>(DEFAULT_FILTERS.env);

    const getComputeFromNav = () => {
        if (navState?.filters && Array.isArray(navState.filters)) {
            const cr = navState.filters.find((r: any) =>
                r.field === 'Compute Type' ||
                r.field === 'ComputeType' ||
                r.field === 'Environment' ||
                r.field === 'compute_type'
            );
            if (cr && cr.value) return String(cr.value);
        }
        return navState?.clusterCategory || navState?.computeType || 'All';
    };

    const [computeType, setComputeType] = useState<string>(getComputeFromNav());
    const [clusterCategory] = useState(navState?.clusterCategory);
    const [workspaceName, setWorkspaceName] = useState<string>('All');
    const [clusterName, setClusterName] = useState<string>('All');

    const [workspaceOptions, setWorkspaceOptions] = useState<any[]>([{ label: 'All', value: 'All', workspaceId: 'All' }]);
    const [clusterNameOptions, setClusterNameOptions] = useState<any[]>([{ label: 'All', value: 'All' }]);

    const [sectorList, setSectorList] = useState<string[]>([]);

    useEffect(() => {
        fetchSectorList({}).then(resp => {
            if (resp?.success) setSectorList(resp?.data || []);
        });
    }, []);

    const setDateStates = useCallback((data: any) => {
        if (data.startDate) setStartDate(data.startDate);
        if (data.endDate) setEndDate(data.endDate);
        if (data.selectedDateInString) setSelectedDateInString(data.selectedDateInString);
    }, []);

    const parseRulesFromNav = useCallback((rules: any[]) => {
        const findValue = (f: string[]) => rules.find((r: any) => f.includes(r.field))?.value;
        const cv = findValue(['Environment', 'Compute Type', 'ComputeType', 'compute_type']);
        const wv = findValue(['Workspace', 'Workspace Name', 'workspace_name', 'Workspace ID', 'workspaceId', 'workspace']);
        const clv = findValue(['Cluster Name', 'cluster_name', 'clusterName']);

        if (cv) setComputeType(String(cv));
        if (wv) setWorkspaceName(String(wv));
        if (clv) setClusterName(String(clv));
    }, []);

    const restoreFilters = useCallback((preservedRaw: string | null, isCustom: boolean) => {
        if (preservedRaw && isCustom) {
            try {
                setDateStates(JSON.parse(preservedRaw));
                restoredRef.current = true;
            } catch (err) {
                console.error('Failed to parse preserved filters', err);
            }
        } else if (!preservedRaw) {
            setDateStates(isCustom ? DEFAULT_FILTERS : (navState || {}));
        }
    }, [navState, DEFAULT_FILTERS, setDateStates]);

    useEffect(() => {
        if (!navState) return;
        const preservedRaw = sessionStorage.getItem(preserveFilterKey);

        if (navState.env) setEnv(navState.env);
        if (navState.sector) setSector(navState.sector);
        if (navState.location) setLocationFilter(navState.location);

        const isCustom = ['custom', 'customDateRange'].includes(navState.selectedDateInString?.trim() || '');
        restoreFilters(preservedRaw, isCustom);

        if (navState.filters && Array.isArray(navState.filters)) {
            parseRulesFromNav(navState.filters);
        }

        initialEnvAppliedRef.current = false;
    }, [navState, preserveFilterKey, restoreFilters, parseRulesFromNav]);

    useEffect(() => {
        const norm = normalizeComputeType(navState?.computeType);
        if (norm && norm !== computeType) {
            setComputeType(norm);
        }
    }, [navState?.computeType, computeType]);

    const [envOptions, setEnvOptions] = useState<{ label: string; value: string }[]>([]);
    useEffect(() => { if (navState?.env) initialEnvAppliedRef.current = true; }, [navState?.env]);

    useEffect(() => {
        const getEnv = async (loc: string) => {
            try {
                const res: any = await workspaceGetEnv(loc);
                const options = [{ label: 'ALL', value: 'both' }, ...(res?.data || [])];
                setEnvOptions(options);
                if (!initialEnvAppliedRef.current && navState?.env) {
                    setEnv(navState?.env);
                    initialEnvAppliedRef.current = true;
                } else {
                    const available = options.map(o => o.value);
                    if (!available.includes(env)) setEnv(available.includes('both') ? 'both' : (available[0] || 'both'));
                }
            } catch (e) {
                console.error(e);
            }
        };
        getEnv(locationFilter);
    }, [locationFilter, navState?.env, env]);

    const [loader, setLoader] = useState(false);
    const [tableData, setTableData] = useState<any[]>([]);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [paginationData, setPaginationData] = useState({ totalElements: 0, totalPages: 0 });
    const [sortingInfo, setSortingInfo] = useState<{ sortBy: string; sortDir: 'asc' | 'desc' | '' }>({ sortBy: '', sortDir: '' });

    useEffect(() => {
        if (!workspaceName || workspaceName === 'all' || workspaceName === 'All') {
            setClusterNameOptions([{ label: 'All', value: 'All' }]);
            setClusterName('All');
            return;
        }
        fetchClusterNameBasedOnWorkspace(workspaceName).then(resp => {
            if (resp?.success && resp?.data) {
                const clusters = resp.data.clusters || [];
                const clusterList = clusters.map((c: any) => ({
                    label: c.cluster_name || c.clusterName,
                    value: c.cluster_name || c.clusterName,
                    cluster_id: c.cluster_id || c.clusterId
                })).filter((c: any) => c.label);
                setClusterNameOptions([{ label: 'All', value: 'All' }, ...clusterList]);
            } else {
                setClusterNameOptions([{ label: 'All', value: 'All' }]);
            }
            setClusterName('All');
        });
    }, [workspaceName]);

    useEffect(() => {
        getWorkspaceOptions(startDate, endDate, env, sector, locationFilter, selectedDateInString).then(resp => {
            if (resp?.success && resp?.data) {
                let options = [];
                if (Array.isArray(resp.data.workspaceName)) {
                    options = resp.data.workspaceName.slice().sort().map((name: string) => ({ label: name, value: name, workspaceId: name }));
                } else if (Array.isArray(resp.data)) {
                    options = resp.data.map((w: any) => ({ label: w.workspace_name, value: w.workspace_name, workspaceId: w.workspace_id }))
                        .filter((w: any) => w.label).sort((a: any, b: any) => a.label.localeCompare(b.label));
                }
                setWorkspaceOptions([{ label: 'All', value: 'all', workspaceId: 'All' }, ...options]);
            } else {
                setWorkspaceOptions([{ label: 'All', value: 'All' }]);
            }
        });
    }, [startDate, endDate, env, sector, locationFilter, selectedDateInString]);

    const mapSortKeyToApi = (sortKey?: string) => {
        if (!sortKey) return undefined;
        const fallback: Record<string, string> = {
            created_date: 'Created Date',
            workspaceId: 'Workspace ID',
            clusterName: 'Cluster Name',
            clusterId: 'Cluster ID',
            workspaceName: 'Workspace Name',
            date: 'Created Date',
            clusterSource: 'Compute Type'
        };
        return fallback[sortKey] || sortKey;
    };

    const processApiResponse = useCallback((respData: any, isInteractive: boolean) => {
        let rawItems = [];
        if (respData) {
            if (Array.isArray(respData.data)) {
                rawItems = respData.data;
            } else if (Array.isArray(respData)) {
                rawItems = respData;
            }
        }

        let total = respData?.pagination?.total ?? rawItems.length;
        let totalPages = respData?.pagination?.totalPages ?? Math.ceil(rawItems.length / pageSize);
        let processedRows = rawItems;

        if (isInteractive) {
            total = rawItems.length;
            totalPages = Math.ceil(total / pageSize);
            processedRows = rawItems.slice((page - 1) * pageSize, page * pageSize);
        }

        setTableData(processedRows.map((r: any) => ({
            workspaceId: r.workspaceId ?? r.workspaceID ?? r.workspace_id ?? '',
            workspaceName: r.workspaceName ?? r.workspace_name ?? '',
            date: r.date ?? r.created_date ?? '',
            computeType: r.clusterSource ?? r.computeType ?? '',
            clusterName: r.clusterName ?? r.cluster_name ?? r.name ?? '',
            clusterId: r.clusterId ?? r.cluster_id ?? ''
        })));
        setPaginationData({ totalElements: total, totalPages });
    }, [page, pageSize]);

    const fetchDetails = useCallback(async () => {
        if (!startDate || !endDate) return;
        setLoader(true);
        try {
            const apiSortField = mapSortKeyToApi(sortingInfo.sortBy);
            const apiSortOrder = sortingInfo.sortDir || undefined;
            const computeTypeParam = (computeType && computeType.toUpperCase() !== 'ALL') ? computeType.toUpperCase() : undefined;
            const isInteractive = computeTypeParam === 'INTERACTIVE';

            let workspaceId: string | undefined = undefined;
            if (workspaceName && workspaceName !== 'all' && workspaceName !== 'All') {
                workspaceId = workspaceOptions.find(o => o.value === workspaceName)?.workspaceId || workspaceName;
            }

            const requestPage = isInteractive ? 1 : page;
            const requestPageSize = isInteractive ? 20000 : pageSize;

            const resp: any = await serviceFunc({
                startDate,
                endDate,
                sector: sector || 'all',
                env: env || 'both',
                computeType: computeTypeParam,
                location: locationFilter || 'South Central US',
                page: requestPage,
                pageSize: requestPageSize,
                sortField: apiSortField,
                sortOrder: apiSortOrder,
                exportType: undefined,
                workspaceID: workspaceId,
                clusterName
            });

            if (resp?.success) {
                processApiResponse(resp.data, isInteractive);
            } else {
                setTableData([]);
                setPaginationData({ totalElements: 0, totalPages: 0 });
            }
        } catch (err) {
            console.error('fetchDetails error', err);
            setTableData([]);
            setPaginationData({ totalElements: 0, totalPages: 0 });
        } finally {
            setLoader(false);
        }
    }, [startDate, endDate, sector, env, computeType, locationFilter, page, pageSize, sortingInfo, workspaceName, workspaceOptions, clusterName, serviceFunc, processApiResponse]);

    useEffect(() => { fetchDetails(); }, [fetchDetails]);

    const handleSorting = (newSorting: { sortBy: string; sortDir: 'asc' | 'desc' | '' }) => {
        setSortingInfo(newSorting?.sortBy && newSorting.sortDir ? newSorting : { sortBy: '', sortDir: '' });
    };

    const handleExportRequest = async () => {
        try {
            const apiSortField = mapSortKeyToApi(sortingInfo.sortBy);
            let workspaceId: string | undefined = undefined;
            if (workspaceName && workspaceName !== 'all' && workspaceName !== 'All') {
                workspaceId = workspaceOptions.find(o => o.value === workspaceName)?.workspaceId || workspaceName;
            }
            await serviceFunc({
                startDate,
                endDate,
                sector: sector || 'all',
                env: env || 'both',
                computeType: (computeType && computeType.toUpperCase() !== 'ALL' ? computeType.toUpperCase() : undefined),
                location: locationFilter || 'South Central US',
                page: 1,
                pageSize: 10000,
                sortField: apiSortField,
                sortOrder: sortingInfo.sortDir || undefined,
                exportType: 'excel',
                workspaceID: workspaceId,
                clusterName
            });
        } catch (err) {
            console.error('Export error', err);
        }
    };

    const handleRunQueryFromBuilder = (rules: QueryRule[]) => {
        const findRuleValue = (fields: string[]) => rules.find(r => fields.includes(r.field))?.value || 'All';
        const cv = findRuleValue(['Environment', 'Compute Type', 'ComputeType', 'compute_type']);
        const wv = findRuleValue(['Workspace', 'Workspace Name', 'workspace_name', 'Workspace ID', 'workspaceId', 'workspace']);
        const clv = findRuleValue(['Cluster Name', 'cluster_name', 'clusterName']);

        setComputeType(String(cv));
        setWorkspaceName(String(wv));
        setClusterName(String(clv));
        setPage(1);
        const newState = { startDate, endDate, env, sector, location: locationFilter, selectedDateInString, computeType: cv, workspaceName: wv, clusterName: clv };
        navigate(routerLocation.pathname, { state: newState, replace: true });
    };

    const handleDateSelect = (stDt: string, edDt: string, selectedDate?: string) => {
        const selected = selectedDate || 'Hrs';
        setSelectedDateInString(selected);
        const yesterday = moment().tz('Asia/Kolkata').subtract(1, 'day');
        const end = yesterday.clone().endOf('day');
        let start;
        if (selected === 'Hrs') start = yesterday.clone().startOf('day');
        else if (selected === '7d') start = yesterday.clone().subtract(6, 'days').startOf('day');
        else if (selected === '30d') start = yesterday.clone().subtract(29, 'days').startOf('day');
        else if (selected === '1M') start = yesterday.clone().subtract(1, 'month').startOf('day');
        else if (selected === '3M') start = yesterday.clone().subtract(89, 'days').startOf('day');
        else if (selected === '6M') start = yesterday.clone().subtract(6, 'months').startOf('day');
        else if (selected === '1Y') start = yesterday.clone().subtract(1, 'year').startOf('day');
        else if (selected === 'custom') {
            const s = moment(stDt).startOf('day');
            const e = moment(edDt).endOf('day');
            setStartDate(s.format('YYYY-MM-DD'));
            setEndDate(e.format('YYYY-MM-DD'));
            setPage(1);
            navigate(routerLocation.pathname, { state: { ...navState, startDate: s.format('YYYY-MM-DD'), endDate: e.format('YYYY-MM-DD'), selectedDateInString: selected }, replace: true });
            return;
        } else start = moment(stDt).startOf('day');

        setStartDate(start.format('YYYY-MM-DD'));
        setEndDate(end.format('YYYY-MM-DD'));
        setPage(1);
        navigate(routerLocation.pathname, { state: { ...navState, startDate: start.format('YYYY-MM-DD'), endDate: end.format('YYYY-MM-DD'), selectedDateInString: selected }, replace: true });
    };

    const handleSectorChange = (value: string) => {
        setSector(value);
        setEnv('both');
        navigate(routerLocation.pathname, { state: { ...navState, startDate, endDate, env: 'both', sector: value, location: locationFilter, selectedDateInString }, replace: true });
    };

    const updateHistoryState = (overrides: any) => {
        const newState = { startDate, endDate, env, sector, location: locationFilter, selectedDateInString, computeType, workspaceName, clusterName, ...overrides };
        navigate(routerLocation.pathname, { state: newState, replace: true });
    };

    const getFormattedDateLabel = (dl: string) => {
        const normalized = dl.replaceAll(/\s+/g, ' ').trim();
        if (/^Week\s*\d+/i.test(normalized)) return normalized;
        if (moment(normalized, 'MMM YYYY', true).isValid()) return normalized;
        if (moment(normalized, 'MMM D YYYY', true).isValid()) return moment(normalized, 'MMM D YYYY').format('MMM D');
        if (moment(normalized, moment.ISO_8601, true).isValid()) return moment(normalized).format('MMM D');
        return null;
    };

    const headerLabel = useMemo(() => {
        const dl = (navState?.dateLabel || '').toString().trim();
        if (dl) {
            const f = getFormattedDateLabel(dl);
            if (f) return f;
        }
        const s = moment(startDate), e = moment(endDate);
        if (s.isValid() && e.isValid() && s.isSame(e, 'day')) return s.format('MMM D');
        if (s.isValid() && e.isValid() && s.isSame(e, 'month')) return s.format('MMM YYYY');
        return startDate ? moment(startDate).format('MMM YYYY') : '';
    }, [navState?.dateLabel, startDate, endDate]);

    return (
        <PageWrapper>
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" sx={{ mb: 2, px: 2 }}>
                <Box sx={{ mb: 2, fontSize: '1.125rem', fontWeight: '600', color: '#003771' }}>
                    <span style={{ cursor: 'pointer', color: '#003771' }} onClick={() => navigate('/databricksADB/clusteranalytics', { state: { startDate, endDate, env, sector, location: locationFilter, selectedDateInString } })}>
                        {navState?.pageHeader ?? 'Cluster Analytics'}
                    </span>
                    {' > '}
                    <span style={{ cursor: 'pointer' }} onClick={() => navigate(backPath, { state: { ...navState, startDate, endDate, env, sector, location: locationFilter, selectedDateInString } })}>
                        {backBreadcrumbName}
                    </span>
                    {' > '}
                    <span>{headerLabel || 'Details'}</span>
                    {((selectedDateInString === 'customDateRange' && startDate && endDate && startDate !== endDate)) && (
                        <span style={{ marginLeft: 8, color: '#003771', fontSize: '0.95rem' }}>({`${startDate} to ${endDate}`})</span>
                    )}
                </Box>
                <Box sx={{ position: 'sticky', top: 64, zIndex: 10 }}>
                    <PageHeader title="" icon={''} lastSync={null}>
                        <FiltersBar
                            location={locationFilter} setLocation={setLocationFilter}
                            env={env} setEnv={setEnv} envOptions={envOptions} setEnvOptions={setEnvOptions}
                            startDate={startDate} endDate={endDate} setStartDate={setStartDate} setEndDate={setEndDate}
                            handleDateSelect={handleDateSelect} selectedDateInString={selectedDateInString} setSelectedDateInString={setSelectedDateInString}
                            sectorName={sector} setSectorName={(v) => handleSectorChange(typeof v === 'function' ? (v as any)(sector) : v)}
                            updateHistoryState={updateHistoryState} sectorOptionsList={sectorList}
                        />
                    </PageHeader>
                </Box>
            </Stack>

            <Box sx={{ px: 2 }}>
                <QueryBuilder
                    title="Run Query" sectorOptions={workspaceOptions} environmentOptions={computeTypeOptions.filter(opt => opt.value !== "SQL")}
                    locationOptions={[]} teamFlagOptions={[]} onRunQuery={handleRunQueryFromBuilder}
                    clusterNameOptions={clusterNameOptions} showClusterName={true} showQueryPreference={false}
                    initialFilters={[
                        { id: '1', operator: 'AND', field: 'Compute Type', value: (clusterCategory || computeType || 'All') },
                        { id: '2', operator: 'AND', field: 'Workspace Name', value: (workspaceName || 'All') },
                    ]}
                />
            </Box>

            <Box sx={{ px: 2 }}>
                {(() => {
                    if (loader) {
                        return <LoaderComponent pt={20} />;
                    }
                    if (tableData.length === 0) {
                        return (
                            <Box sx={{ textAlign: 'center', py: 10, backgroundColor: 'white', borderRadius: 1, border: '1px solid #e0e0e0', mx: 2 }}>
                                <div style={{ fontSize: '1.2rem', color: '#666', marginBottom: 8 }}>No Data Available</div>
                                <div style={{ fontSize: '0.9rem', color: '#999' }}>No cluster details found for the selected range.</div>
                            </Box>
                        );
                    }
                    return (
                        <CustomizedDataGrid
                            title={`New Clusters (${paginationData.totalElements.toLocaleString()} )`}
                            tableColumns={getColumns()} tableData={tableData} tableType={tableType}
                            defaultColumnNames={defaultColumnNames} page={page} pageSize={pageSize}
                            paginationData={paginationData} listLength={tableData.length}
                            handlepageSizeChange={(e: any) => { setPageSize(Number(e.target.value)); setPage(1); }}
                            handlePageChange={(_: any, v: number) => setPage(v)}
                            isDateRangeFilter={false} isDisabled={false} isFetching={loader}
                            showExport={true} exportTableData={handleExportRequest}
                            onSorting={handleSorting} currSorting={sortingInfo} onFilterCheck={() => { }}
                        />
                    );
                })()}
            </Box>
        </PageWrapper>
    );
};

export default ClusterDetailsTableBase;
